import { filterBrowsableProducts } from './catalogProductShape';
import { idsMatch, productMatchesSelectedCategory } from './catalogIds';

/** Sale price shown on product cards (already shaped in DataContext). */
export function getProductDisplayPrice(product) {
  const n = parseFloat(product?.price);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Products that match sidebar category/store filters — same pool users browse before price filter. */
export function filterProductsForPriceSlider(products = [], filters = {}, categories = []) {
  let list = filterBrowsableProducts(products);
  const category = filters?.category;
  if (category && category !== 'all') {
    list = list.filter((p) => productMatchesSelectedCategory(p, category, categories));
  }
  const store = filters?.store;
  if (store && store !== 'all') {
    const wantStore = String(store).trim();
    list = list.filter((p) => {
      const candidateStores = [
        p.storeId,
        p.store?._id,
        p.store?.id,
        p.vendor?._id,
        p.vendor?.id
      ]
        .map((v) => (v != null ? String(v).trim() : ''))
        .filter(Boolean);
      return candidateStores.some((id) => idsMatch(id, wantStore));
    });
  }
  return list;
}

/**
 * Slider step sized for ~40–60 stops across the range (finer than fixed 500/1000 jumps).
 */
export function getCatalogPriceStep(maxPrice = 1000) {
  const m = Math.max(1, Number(maxPrice) || 1000);
  const rough = m / 50;
  const niceSteps = [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000];
  for (const step of niceSteps) {
    if (rough <= step) return step;
  }
  return 5000;
}

export function snapPriceToStep(value, step = 1) {
  const s = Math.max(1, Number(step) || 1);
  const v = Math.max(0, Number(value) || 0);
  return Math.round(v / s) * s;
}

/**
 * Upper bound for the price slider from real storefront prices (full range of visible catalog).
 */
export function getCatalogMaxPrice(products = []) {
  const prices = (products || [])
    .map(getProductDisplayPrice)
    .filter((n) => n != null)
    .sort((a, b) => a - b);

  if (!prices.length) return 1000;

  const highest = prices[prices.length - 1];
  const step = getCatalogPriceStep(highest);
  return Math.max(step, Math.ceil(highest / step) * step);
}

export function isPriceSliderActive(filters, catalogMax) {
  const max = catalogMax ?? Infinity;
  const min = filters?.priceMin ?? 0;
  const cap = filters?.priceMax;
  if (cap == null) return false;
  return min > 0 || cap < max;
}

/** Whether a product falls within the active price slider range. */
export function productMatchesPriceFilter(product, filters, catalogMax) {
  if (filters?.priceMax == null) return true;
  const price = getProductDisplayPrice(product);
  if (price == null) return false;
  const min = filters.priceMin ?? 0;
  const max = filters.priceMax ?? catalogMax ?? Infinity;
  return price >= min && price <= max;
}
